import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Car, QrCode, ScanLine, PauseCircle, TrendingUp, Plus, Sticker, Sparkles, Camera, ChevronRight, Building2, DollarSign, Clock, HelpCircle, BellRing, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { openTutorial } from "@/components/TutorialOverlay";
import { TUTORIAL_LS_KEY } from "@/lib/tutorial";

export default function Dashboard() {
  const { activeProperty, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [billing, setBilling] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [recent, setRecent] = useState([]);
  const [missingPhotoVehicles, setMissingPhotoVehicles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [paying, setPaying] = useState(false);

  const paySetupFeeNow = async () => {
    if (!activeProperty || paying) return;
    setPaying(true);
    try {
      const { data } = await api.post("/billing/setup-fee", {
        property_id: activeProperty.id,
        origin_url: window.location.origin,
        redirect_after: "onboard",
      });
      // Take the user straight to Stripe — no intermediate page.
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(false);
    }
  };

  useEffect(() => {
    if (!activeProperty) return;
    api
      .get(`/stats?property_id=${activeProperty.id}`)
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api
      .get(`/billing/summary?property_id=${activeProperty.id}`)
      .then((r) => setBilling(r.data))
      .catch(() => setBilling(null));
    // Full billing details — used for the renewal banner (3 days before expiry)
    api
      .get(`/billing/details?property_id=${activeProperty.id}`)
      .then((r) => setBillingDetails(r.data))
      .catch(() => setBillingDetails(null));
    api
      .get(`/billing/invoices?property_id=${activeProperty.id}`)
      .then((r) => setInvoices(r.data || []))
      .catch(() => setInvoices([]));
    api
      .get(`/notifications?property_id=${activeProperty.id}&limit=5`)
      .then((r) => setNotifications(r.data || []))
      .catch(() => setNotifications([]));
    api
      .get(`/scan-logs?property_id=${activeProperty.id}&limit=6`)
      .then((r) => setRecent(r.data || []))
      .catch(() => {});
    api
      .get(`/vehicles?property_id=${activeProperty.id}`)
      .then((r) => {
        const missing = (r.data || []).filter(
          (v) => !v.images || v.images.length === 0
        );
        setMissingPhotoVehicles(missing);
      })
      .catch(() => setMissingPhotoVehicles([]));
  }, [activeProperty]);

  // Auto-trigger the tutorial for new users who haven't seen it yet AND
  // haven't completed the "core setup" (no active vehicles). We delay a
  // beat so the dashboard renders before the spotlight locks on.
  useEffect(() => {
    if (!user || !stats) return;
    const seen = (() => {
      try { return localStorage.getItem(TUTORIAL_LS_KEY); } catch { return null; }
    })();
    const done = seen || user.tutorial_completed_at;
    const coreIncomplete = (stats.active_vehicles || 0) === 0;
    if (!done && coreIncomplete) {
      const t = setTimeout(() => openTutorial(0), 800);
      return () => clearTimeout(t);
    }
  }, [user, stats]);

  if (!activeProperty) {
    return (
      <div className="max-w-xl mx-auto text-center py-24">
        <h2 className="font-display text-2xl font-bold">No property yet</h2>
        <p className="text-[#71717A] mt-2 mb-6">Create your first property to start adding tenants and vehicles.</p>
        <Link to="/onboarding">
          <Button className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-12" data-testid="create-first-property">
            <Plus className="w-4 h-4 mr-2" /> Create property
          </Button>
        </Link>
      </div>
    );
  }

  const tiles = [
    {
      k: "active_cars",
      label: "Active cars",
      icon: Building2,
      color: "#0F8F4F",
      bg: "bg-[#E6F4EC]",
      border: "border-[#0F8F4F]/20",
      to: "/dashboard/billing",
      value: billing ? (billing.active_cars ?? billing.billable_units) : null,
    },
    {
      k: "monthly_total",
      label: "Monthly est.",
      icon: DollarSign,
      color: "#0A6B3B",
      bg: "bg-[#D1FAE5]",
      border: "border-[#0F8F4F]/30",
      to: "/dashboard/billing",
      value: billing
        ? (billing.quote_required ? "Quote" : `$${billing.monthly_total.toFixed(2)}`)
        : null,
    },
    {
      k: "total_tenants",
      label: "Tenants",
      icon: Users,
      color: "#0EA5E9",
      bg: "bg-[#E0F2FE]",
      border: "border-[#0EA5E9]/20",
      to: "/dashboard/tenants",
    },
    {
      k: "total_vehicles",
      label: "Vehicles",
      icon: Car,
      color: "#A855F7",
      bg: "bg-[#F3E8FF]",
      border: "border-[#A855F7]/20",
      to: "/dashboard/vehicles",
    },
    {
      k: "active_vehicles",
      label: "Active QRs",
      icon: QrCode,
      color: "#10B981",
      bg: "bg-[#D1FAE5]",
      border: "border-[#10B981]/30",
      to: "/dashboard/qr-codes",
    },
    {
      k: "suspended_vehicles",
      label: "Suspended",
      icon: PauseCircle,
      color: "#EF4444",
      bg: "bg-[#FEE2E2]",
      border: "border-[#EF4444]/20",
      to: "/dashboard/suspended",
    },
    {
      k: "scans_today",
      label: "Scans today",
      icon: ScanLine,
      color: "#F59E0B",
      bg: "bg-[#FEF3C7]",
      border: "border-[#F59E0B]/20",
      to: "/dashboard/scan-logs",
    },
    {
      k: "total_scans",
      label: "Total scans",
      icon: TrendingUp,
      color: "#3F3F46",
      bg: "bg-[#F4F4F5]",
      border: "border-[#3F3F46]/20",
      to: "/dashboard/scan-logs",
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="mono-tiny text-[#71717A]">Overview</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
            {activeProperty.property_name}
          </h1>
          <div className="flex items-center gap-3 flex-wrap mt-1">
            <div className="text-sm text-[#71717A]">
              {activeProperty.address}, {activeProperty.city}, {activeProperty.state} {activeProperty.zip}
            </div>
            {billing && (billing.active_cars ?? billing.billable_units) > 0 && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#0F8F4F]/10 text-[#0F8F4F] border border-[#0F8F4F]/30"
                data-testid="property-unit-badge"
              >
                <Building2 className="w-3 h-3" strokeWidth={2} />
                {billing.active_cars ?? billing.billable_units} car{(billing.active_cars ?? billing.billable_units) === 1 ? "" : "s"}
                {" · "}
                {billing.quote_required
                  ? "enterprise quote"
                  : billing.free_month_active
                  ? `FREE this month (was $${(billing.monthly_full_total ?? billing.monthly_total).toFixed(2)})`
                  : `$${billing.monthly_total.toFixed(2)}/mo`}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => openTutorial(0)}
            className="h-11"
            data-testid="dashboard-replay-tutorial"
          >
            <HelpCircle className="w-4 h-4 mr-2" /> Replay tutorial
          </Button>
          <Link to="/dashboard/onboard">
            <Button className="bg-[#0F8F4F] hover:bg-[#0A6B3B] h-11" data-testid="dashboard-add-tenant">
              <Plus className="w-4 h-4 mr-2" /> Add tenant
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {tiles.map((t) => (
          <Link
            key={t.k}
            to={t.to}
            data-testid={`stat-${t.k}`}
            className={`block ${t.bg} border ${t.border} rounded-lg p-5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`}
          >
            <t.icon className="w-4 h-4" strokeWidth={1.5} style={{ color: t.color }} />
            <div className="mono-tiny text-[#71717A] mt-3">{t.label}</div>
            <div className="font-display text-2xl md:text-3xl font-bold mt-1">
              {t.value !== undefined
                ? t.value === null
                  ? "—"
                  : t.value
                : stats
                ? stats[t.k] ?? 0
                : "—"}
            </div>
          </Link>
        ))}
      </div>

      {/* Renewal nudge — appears in the last 3 days of the current cycle.
          Only shows when there's a real billable amount (units > 0); a $0
          renewal isn't worth nagging about. Hidden for demo properties. */}
      {(() => {
        if (!billingDetails || activeProperty.is_demo) return null;
        const sub = billingDetails.subscription || {};
        if (!sub.active || !sub.active_until) return null;
        if (((billingDetails.active_cars ?? billingDetails.billable_units) || 0) === 0) return null;
        const ms = new Date(sub.active_until).getTime() - Date.now();
        const daysLeft = Math.ceil(ms / (1000 * 60 * 60 * 24));
        if (daysLeft > 3 || daysLeft < 0) return null;
        return (
          <div
            className="bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-xl p-5 md:p-6"
            data-testid="renewal-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-[#F59E0B]/20 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-[#92400E]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-[#7C2D12]">
                  Renews in {daysLeft} day{daysLeft === 1 ? "" : "s"} ·{" "}
                  ${(billingDetails.monthly_total || 0).toFixed(2)} — keep your account active
                </div>
                <div className="text-sm text-[#92400E] mt-1" data-testid="renewal-banner-detail">
                  Current cycle ends {new Date(sub.active_until).toLocaleDateString()}.
                  Pay now to continue uninterrupted access.
                </div>
              </div>
              <Link to="/dashboard/billing">
                <Button
                  className="h-11 bg-[#0F8F4F] hover:bg-[#0A6B3B] shrink-0"
                  data-testid="renewal-banner-pay-now"
                >
                  Pay now
                </Button>
              </Link>
            </div>
          </div>
        );
      })()}

      {/* Setup-fee banner — shows until property has paid the $99 setup fee.
          New onboarding flow: create property → pay $99 → access dashboard.
          Demo properties skip this banner since they're pre-activated. */}
      {/* === Billing email health warning ===
          Surfaces only when the last 2+ emails to billing_contact bounced
          or failed. Auto-clears the moment a future email is delivered.*/}
      {!activeProperty.is_demo && billingDetails?.billing_email_health?.ok === false && (
        <div
          className="bg-[#FEE2E2] border border-[#EF4444]/40 rounded-xl p-5 md:p-6"
          data-testid="billing-email-health-banner"
        >
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-[#EF4444]/15 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-[#B91C1C]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-lg text-[#7F1D1D] leading-tight">
                Billing email issue — update contact to avoid missed invoices
              </div>
              <div className="text-sm text-[#7F1D1D] mt-1">
                The last {billingDetails.billing_email_health.recent_bounces}{" "}
                emails to your billing contact bounced. Update the address now
                so invoices, reminders, and payment confirmations reach you.
              </div>
              {billingDetails.billing_email_health.last_bounce_reason && (
                <div className="mono-tiny text-[#B91C1C] mt-2 truncate" title={billingDetails.billing_email_health.last_bounce_reason}>
                  Last reason: {billingDetails.billing_email_health.last_bounce_reason}
                </div>
              )}
            </div>
            <Link to="/dashboard/settings" className="shrink-0">
              <Button
                className="h-11 w-full md:w-auto bg-[#B91C1C] hover:bg-[#7F1D1D] text-white"
                data-testid="billing-email-update-cta"
              >
                Update Email
              </Button>
            </Link>
          </div>
        </div>
      )}

      {!activeProperty.is_demo && billing && !billing.setup_fee_paid && billing.setup_fee_paid !== undefined && (
        <div
          className={
            activeProperty.setup_fee_invoice_sent_at
              ? "bg-[#EFF6FF] border border-[#3B82F6]/30 rounded-xl p-5 md:p-6"
              : "bg-[#FFFBEA] border border-[#FACC15]/40 rounded-xl p-5 md:p-6"
          }
          data-testid="setup-fee-banner"
        >
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div
              className={
                activeProperty.setup_fee_invoice_sent_at
                  ? "w-10 h-10 rounded-md bg-[#3B82F6]/15 flex items-center justify-center shrink-0"
                  : "w-10 h-10 rounded-md bg-[#FACC15]/20 flex items-center justify-center shrink-0"
              }
            >
              <DollarSign
                className={
                  activeProperty.setup_fee_invoice_sent_at
                    ? "w-5 h-5 text-[#1D4ED8]"
                    : "w-5 h-5 text-[#92400E]"
                }
                strokeWidth={1.5}
              />
            </div>
            <div className="flex-1 min-w-0">
              {activeProperty.setup_fee_invoice_sent_at ? (
                <>
                  <div
                    className="font-display font-bold text-xl text-[#1E3A8A] leading-tight"
                    data-testid="setup-fee-invoice-pending-title"
                  >
                    Invoice sent — awaiting payment
                  </div>
                  <div className="mono-tiny text-[#1D4ED8] mt-1">
                    $99 setup fee · invoice emailed
                  </div>
                  <div className="text-sm text-[#1E40AF] mt-2">
                    Tenant and vehicle setup unlocks once the invoice is marked
                    paid. Pay by card now to activate instantly.
                  </div>
                </>
              ) : (
                <>
                  <div className="font-display font-bold text-xl text-[#7C2D12] leading-tight">
                    Activate Your Service
                  </div>
                  <div className="mono-tiny text-[#92400E] mt-1">
                    Complete Setup — $99
                  </div>
                  <div className="text-sm text-[#92400E] mt-2">
                    One-time charge includes inkless mini printer, QR sticker
                    supply, and welcome packet. After payment you can activate
                    vehicle QR codes.
                  </div>
                </>
              )}
            </div>
            <Button
              onClick={paySetupFeeNow}
              disabled={paying}
              className="h-11 w-full md:w-auto bg-[#0F8F4F] hover:bg-[#0A6B3B] shrink-0"
              data-testid="dashboard-pay-setup-fee"
            >
              {paying ? "Opening Stripe…" : "Pay Now"}
            </Button>
          </div>
        </div>
      )}

      {/* === Invoices snapshot — open invoice + last 2 paid ===
          Read-only summary; full management still lives on /billing. */}
      {(() => {
        const open = invoices.find(
          (i) => i.status === "pending" || i.status === "sent" || i.status === "overdue"
        );
        const paid = invoices.filter((i) => i.status === "paid").slice(0, 2);
        const visible = [open, ...paid].filter(Boolean);
        if (visible.length === 0) return null;
        return (
          <div
            className="bg-white border border-[#E4E4E7] rounded-xl p-5 md:p-6"
            data-testid="dashboard-invoices-card"
          >
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
                <div className="mono-tiny text-[#71717A]">Invoices</div>
              </div>
              <Link
                to="/dashboard/billing"
                className="text-xs font-semibold text-[#0F8F4F] hover:underline"
                data-testid="dashboard-invoices-view-all"
              >
                View all →
              </Link>
            </div>
            <div className="divide-y divide-[#E4E4E7]">
              {visible.map((inv) => {
                const isOpen = inv.status !== "paid";
                return (
                  <div
                    key={inv.id}
                    className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                    data-testid={`dashboard-invoice-${inv.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium font-mono text-sm">{inv.invoice_number}</span>
                        <DashInvoicePill status={inv.status} />
                      </div>
                      <div className="mono-tiny text-[#71717A] mt-1">
                        {inv.status === "paid"
                          ? `Paid ${new Date(inv.paid_at || inv.created_at).toLocaleDateString()}`
                          : `Due ${new Date(inv.due_date).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:justify-end gap-3 md:gap-4 shrink-0">
                      <div className="font-display font-bold text-lg">
                        ${(inv.total || 0).toFixed(2)}
                      </div>
                      <Link
                        to="/dashboard/billing"
                        data-testid={`dashboard-invoice-view-${inv.id}`}
                        className="shrink-0"
                      >
                        <Button
                          size="sm"
                          className={
                            isOpen
                              ? "h-9 px-4 bg-[#0F8F4F] hover:bg-[#0A6B3B] text-white shadow-sm"
                              : "h-9 px-4"
                          }
                          variant={isOpen ? "default" : "outline"}
                        >
                          View invoice
                          <ChevronRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* === Recent notifications — last 5 emails sent for this property === */}
      {notifications.length > 0 && (
        <div
          className="bg-white border border-[#E4E4E7] rounded-xl p-5 md:p-6"
          data-testid="dashboard-notifications-card"
        >
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <BellRing className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
              <div className="mono-tiny text-[#71717A]">Recent notifications</div>
            </div>
            <span className="mono-tiny text-[#71717A]">
              {notifications[0].provider === "resend" ? "Live · Resend" : "Mock"}
            </span>
          </div>
          <div className="divide-y divide-[#E4E4E7]">
            {notifications.map((n) => (
              <div
                key={n.id}
                className="py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                data-testid={`dashboard-notification-${n.id}`}
                data-notif-kind={n.kind}
                data-notif-status={n.status}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <NotifKindPill kind={n.kind} />
                    <span className="text-sm text-[#09090B] truncate">{n.subject || "(no subject)"}</span>
                  </div>
                  <div className="mono-tiny text-[#71717A] mt-0.5 truncate">
                    To {n.to} · {new Date(n.sent_at).toLocaleString()}
                  </div>
                </div>
                <NotifStatusPill status={n.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo reminder — soft nudge when vehicles are missing photos.
          Shows up to 3 inline; clickable rows route to the tenant edit
          flow where the user can upload photos to that vehicle. */}
      {missingPhotoVehicles.length > 0 && (
        <div
          className="bg-[#F0F9FF] border border-[#0EA5E9]/30 rounded-xl p-5 md:p-6"
          data-testid="photo-reminder-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-[#0EA5E9]/15 flex items-center justify-center shrink-0">
              <Camera className="w-5 h-5 text-[#0369A1]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[#0C4A6E]">
                Add photos to improve verification accuracy
              </div>
              <div className="text-sm text-[#0369A1]/80 mt-1">
                {missingPhotoVehicles.length} vehicle
                {missingPhotoVehicles.length === 1 ? " is" : "s are"} missing photos.
                Photos help security and staff confirm the actual car at a glance.
              </div>
              <div className="mt-3 space-y-1.5">
                {missingPhotoVehicles.slice(0, 3).map((v) => (
                  <Link
                    key={v.id}
                    to={`/dashboard/onboard?edit_tenant=${v.tenant_id}`}
                    data-testid={`photo-reminder-vehicle-${v.id}`}
                    className="flex items-center justify-between gap-3 bg-white border border-[#0EA5E9]/20 hover:border-[#0EA5E9] rounded-md px-3 py-2 transition-colors"
                  >
                    <span className="text-sm text-[#09090B] truncate">
                      <b>{v.year} {v.make} {v.model}</b>
                      <span className="text-[#71717A] font-mono ml-2">{v.license_plate}</span>
                    </span>
                    <span className="inline-flex items-center text-xs font-semibold text-[#0369A1] shrink-0">
                      Add photos <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                    </span>
                  </Link>
                ))}
                {missingPhotoVehicles.length > 3 && (
                  <Link
                    to="/dashboard/vehicles"
                    className="block text-xs font-semibold text-[#0369A1] hover:underline pt-1"
                    data-testid="photo-reminder-view-all"
                  >
                    View all {missingPhotoVehicles.length} →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticker upsell banner — appears once 5+ active QRs */}
      {stats && stats.active_vehicles >= 5 && (
        <Link
          to="/dashboard/stickers"
          className="block"
          data-testid="sticker-upsell-banner"
        >
          <div className="rounded-xl p-6 md:p-7 flex items-center justify-between gap-4 flex-wrap border bg-[#0F8F4F]/5 border-[#0F8F4F]/30">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-md flex items-center justify-center bg-[#0F8F4F] text-white">
                <Sticker className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div>
                <div className="mono-tiny text-[#71717A]">You're at 5+ active QRs</div>
                <div className="font-display text-lg font-bold mt-1">
                  Order printed QR sticker packs
                </div>
                <div className="text-sm text-[#3F3F46] mt-1">
                  $25 per pack of 25 · ships within 3–5 business days
                </div>
              </div>
            </div>
            <span className="text-sm font-semibold px-4 py-2 rounded-md bg-[#0F8F4F] text-white">
              View packs
            </span>
          </div>
        </Link>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white border border-[#E4E4E7] rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="mono-tiny text-[#71717A]">Recent scans</div>
              <h2 className="font-display text-xl font-bold mt-1">Latest verification activity</h2>
            </div>
            <Link to="/dashboard/scan-logs">
              <Button variant="outline" className="h-10" data-testid="dashboard-view-all-scans">
                View all
              </Button>
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-sm text-[#71717A] py-10 text-center">
              No scans yet. Activate some vehicles to generate QR codes.
            </div>
          ) : (
            <div className="divide-y divide-[#E4E4E7]">
              {recent.map((s) => (
                <div key={s.id} className="py-3 flex items-center justify-between gap-4" data-testid={`recent-scan-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">
                      {s.vehicle_summary || s.qr_code}
                    </div>
                    <div className="mono-tiny text-[#71717A] truncate">{s.user_agent?.slice(0, 60)}</div>
                  </div>
                  <div className="mono-tiny text-[#71717A] shrink-0">
                    {new Date(s.scanned_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-[#09090B] text-white rounded-lg p-6">
          <div className="mono-tiny text-white/50">Signed in as</div>
          <div className="font-display text-xl font-bold mt-2">{user?.name}</div>
          <div className="text-sm text-white/60">{user?.email}</div>
          <div className="mono-tiny text-white/50 mt-6">Role</div>
          <div className="font-display text-lg font-bold mt-1 capitalize">
            {user?.role?.replace("_", " ")}
          </div>
          <div className="mt-8 space-y-2">
            <Link to="/dashboard/tenants" className="block">
              <Button variant="secondary" className="w-full h-11 bg-white text-[#09090B] hover:bg-white/90" data-testid="quick-tenants">
                Manage tenants
              </Button>
            </Link>
            <Link to="/dashboard/billing" className="block">
              <Button variant="ghost" className="w-full h-11 text-white hover:bg-white/10 hover:text-white" data-testid="quick-billing">
                View billing
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function DashInvoicePill({ status }) {
  const map = {
    pending: { label: "Pending", cls: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30" },
    issued: { label: "Issued", cls: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30" },
    sent: { label: "Sent", cls: "bg-[#DBEAFE] text-[#1E40AF] border-[#3B82F6]/30" },
    processing: { label: "Payment processing", cls: "bg-[#EFF6FF] text-[#1D4ED8] border-[#3B82F6]/40" },
    paid: { label: "Paid", cls: "bg-[#0F8F4F]/15 text-[#0F8F4F] border-[#0F8F4F]/30" },
    overdue: { label: "Overdue", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#EF4444]/30" },
    void: { label: "Void", cls: "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]" },
  };
  const m = map[status] || map.pending;
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${m.cls}`}
      data-testid={`dashboard-invoice-status-${status}`}
    >
      {m.label}
    </span>
  );
}

const NOTIF_KIND_LABEL = {
  invoice_issued: "Invoice issued",
  invoice_due_reminder: "Due reminder",
  invoice_paid: "Payment received",
};

function NotifKindPill({ kind }) {
  const cls = {
    invoice_issued: "bg-[#DBEAFE] text-[#1E40AF] border-[#3B82F6]/30",
    invoice_due_reminder: "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30",
    invoice_paid: "bg-[#0F8F4F]/15 text-[#0F8F4F] border-[#0F8F4F]/30",
  }[kind] || "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]";
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${cls}`}
      data-testid={`dashboard-notification-kind-${kind}`}
    >
      {NOTIF_KIND_LABEL[kind] || kind}
    </span>
  );
}

function NotifStatusPill({ status }) {
  const map = {
    delivered: { label: "Delivered", icon: CheckCircle2, cls: "text-[#0F8F4F]" },
    sent: { label: "Sent", icon: CheckCircle2, cls: "text-[#0F8F4F]" },
    sent_mock: { label: "Mock · sent", icon: CheckCircle2, cls: "text-[#71717A]" },
    bounced: { label: "Bounced", icon: AlertTriangle, cls: "text-[#B91C1C]" },
    failed: { label: "Failed", icon: AlertTriangle, cls: "text-[#B91C1C]" },
    delayed: { label: "Delayed", icon: Clock, cls: "text-[#92400E]" },
    queued: { label: "Queued", icon: Clock, cls: "text-[#71717A]" },
  };
  const m = map[status] || { label: status, icon: Clock, cls: "text-[#71717A]" };
  const Ico = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${m.cls} shrink-0`}
      data-testid={`dashboard-notification-status-${status}`}
    >
      <Ico className="w-3.5 h-3.5" strokeWidth={2} />
      {m.label}
    </span>
  );
}